import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RequestStatus} from '../../utils/Enums';
import {apiClient} from '../../utils/ApiClient';
import {ClientContact} from '../../models/ClientContact';
import {ModifyClientContactFormProps} from "../../components/clientContacts/ModifyClientContactScreen";

interface ClientContactsState {
    clientContacts: ClientContact[];
    currentClientContact: ClientContact | null;
    getClientContactsStatus: RequestStatus;
    createClientContactStatus: RequestStatus;
    updateClientContactStatus: RequestStatus;
    deleteClientContactStatus: RequestStatus;
    getCurrentClientContactStatus: RequestStatus;
    errors: string[];
    clientId: number | null; // Store the current client's ID
}

const initialState: ClientContactsState = {
    clientContacts: [],
    currentClientContact: null,
    getClientContactsStatus: RequestStatus.Idle,
    createClientContactStatus: RequestStatus.Idle,
    updateClientContactStatus: RequestStatus.Idle,
    deleteClientContactStatus: RequestStatus.Idle,
    getCurrentClientContactStatus: RequestStatus.Idle,
    errors: [],
    clientId: null,
};

export const getClientContacts = createAsyncThunk(
    'clientContacts/getClientContacts',
    async ({clientId}: { clientId: number }, {rejectWithValue}) => {
        const response = await apiClient.get(`clients/${clientId}/contacts`);
        return response.data;
    }
);

export const createClientContact = createAsyncThunk(
    'clientContacts/createClientContact',
    async ({clientId, clientContactData}: { clientId: number; clientContactData: any }, {rejectWithValue}) => {
        const response = await apiClient.post(`clients/${clientId}/contacts/store`, clientContactData);
        return response.data;
    }
);

export const updateClientContact = createAsyncThunk(
    'clientContacts/updateClientContact',
    async ({clientId, contactId, clientContactData}: { clientId: number; contactId: number, clientContactData: ModifyClientContactFormProps }, {rejectWithValue}) => {
        const response = await apiClient.put(`clients/${clientId}/contacts/${contactId}`, clientContactData);
        return response.data;
    }
);

export const deleteClientContact = createAsyncThunk(
    'clientContacts/deleteClientContact',
    async ({clientId, contactId}: { clientId: number, contactId: number }, {rejectWithValue}) => {
        const response = await apiClient.delete(`clients/${clientId}/contacts/${contactId}`);
        return response.data;
    }
);

export const getCurrentClientContact = createAsyncThunk(
    'clientContacts/getCurrentClientContact',
    async ({clientId, contactId}: { clientId: number, contactId: number }, {rejectWithValue}) => {
        const response = await apiClient.get(`clients/${clientId}/contacts/${contactId}`);
        return response.data;
    }
);

const clientContactsSlice = createSlice({
    name: 'clientContacts',
    initialState,
    reducers: {
        resetCreateClientContactStatus: (state) => {
            state.createClientContactStatus = RequestStatus.Idle;
            state.currentClientContact = null;
        },
        resetUpdateClientContactStatus: (state) => {
            state.updateClientContactStatus = RequestStatus.Idle;
            state.currentClientContact = null;
        },
        resetDeleteClientContactStatus: (state) => {
            state.deleteClientContactStatus = RequestStatus.Idle;
            state.currentClientContact = null;
        },
        setClientId: (state, action) => {
            state.clientId = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getClientContacts.pending, (state) => {
                state.getClientContactsStatus = RequestStatus.Loading;
            })
            .addCase(getClientContacts.fulfilled, (state, action) => {
                state.getClientContactsStatus = RequestStatus.Success;
                state.clientContacts = action.payload;
            })
            .addCase(getClientContacts.rejected, (state) => {
                state.getClientContactsStatus = RequestStatus.Failed;
            })
            .addCase(createClientContact.pending, (state) => {
                state.createClientContactStatus = RequestStatus.Loading;
            })
            .addCase(createClientContact.fulfilled, (state) => {
                state.createClientContactStatus = RequestStatus.Success;
            })
            .addCase(createClientContact.rejected, (state, action) => {
                state.createClientContactStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })
            .addCase(updateClientContact.pending, (state) => {
                state.updateClientContactStatus = RequestStatus.Loading;
            })
            .addCase(updateClientContact.fulfilled, (state, action) => {
                state.updateClientContactStatus = RequestStatus.Success;
                state.currentClientContact = action.payload;
            })
            .addCase(updateClientContact.rejected, (state, action) => {
                state.updateClientContactStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })
            .addCase(deleteClientContact.pending, (state) => {
                state.deleteClientContactStatus = RequestStatus.Loading;
            })
            .addCase(deleteClientContact.fulfilled, (state) => {
                state.deleteClientContactStatus = RequestStatus.Success;
            })
            .addCase(deleteClientContact.rejected, (state) => {
                state.deleteClientContactStatus = RequestStatus.Failed;
            })
            .addCase(getCurrentClientContact.pending, (state) => {
                state.getCurrentClientContactStatus = RequestStatus.Loading;
            })
            .addCase(getCurrentClientContact.fulfilled, (state, action) => {
                state.currentClientContact = action.payload;
                state.getCurrentClientContactStatus = RequestStatus.Success;
            })
            .addCase(getCurrentClientContact.rejected, (state) => {
                state.getCurrentClientContactStatus = RequestStatus.Failed;
            });
    },
});

export const {
    resetCreateClientContactStatus,
    resetUpdateClientContactStatus,
    resetDeleteClientContactStatus,
} = clientContactsSlice.actions;

export default clientContactsSlice.reducer;
